import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeftIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    ClockIcon,
    ShieldCheckIcon,
    CheckCircleIcon,
    XCircleIcon,
    PencilSquareIcon,
    KeyIcon,
    UserCircleIcon,
    IdentificationIcon,
    LockClosedIcon,
    GlobeAltIcon,
    NoSymbolIcon,
    PowerIcon,
    TrashIcon,
    Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/features/auth/state/useAuth";
import { getAdminDetails, getPermissionsCatalog } from "../api/adminsApi";
import { AdminStatusModal } from "../ui/AdminStatusModal";
import { DeleteAdminModal } from "../ui/DeleteAdminModal";
import { PermissionSummary } from "../ui/PermissionCatalogEditor";
import { ResetPasswordModal } from "../ui/ResetPasswordModal";
import type { Admin, AdminStatus, UpdateAdminStatusAction } from "../types";

const RoleBadge = ({ role }: { role: string }) => {
    const roleConfig = {
        SUPER_ADMIN: {
            bg: "bg-rose-100 dark:bg-rose-900/30",
            text: "text-rose-700 dark:text-rose-400",
            ring: "ring-rose-500/30",
            glow: "shadow-rose-500/25 shadow-xl",
            gradient: "from-rose-500 to-rose-600",
        },
        ADMIN: {
            bg: "bg-emerald-100 dark:bg-emerald-900/30",
            text: "text-emerald-700 dark:text-emerald-400",
            ring: "ring-emerald-500/30",
            glow: "shadow-emerald-500/25 shadow-xl",
            gradient: "from-emerald-500 to-emerald-600",
        },
        MODERATOR: {
            bg: "bg-blue-100 dark:bg-blue-900/30",
            text: "text-blue-700 dark:text-blue-400",
            ring: "ring-blue-500/30",
            glow: "",
            gradient: "from-blue-500 to-blue-600",
        },
    };

    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.MODERATOR;

    return (
        <span
            className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold ring-1 ring-inset ${config.bg} ${config.text} ${config.ring} ${config.glow}`}
        >
            <ShieldCheckIcon className="h-4 w-4" />
            {role.replace("_", " ")}
        </span>
    );
};

const StatusBadge = ({ status }: { status: string }) => {
    const statusConfig = {
        ACTIVE: {
            bg: "bg-emerald-100 dark:bg-emerald-900/30",
            text: "text-emerald-700 dark:text-emerald-400",
            dot: "bg-emerald-500",
        },
        DEACTIVATED: {
            bg: "bg-slate-100 dark:bg-slate-800",
            text: "text-slate-700 dark:text-slate-300",
            dot: "bg-slate-400",
        },
        SUSPENDED: {
            bg: "bg-amber-100 dark:bg-amber-900/30",
            text: "text-amber-700 dark:text-amber-400",
            dot: "bg-amber-500",
        },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DEACTIVATED;

    return (
        <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${config.bg} ${config.text}`}
        >
            <span className={`h-2 w-2 rounded-full ${config.dot}`}></span>
            {status}
        </span>
    );
};

const InfoCard = ({
    icon: Icon,
    label,
    value,
    valueColor = "text-slate-900 dark:text-white",
}: {
    icon: React.ElementType;
    label: string;
    value: string | React.ReactNode;
    valueColor?: string;
}) => (
    <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
            <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
            <p className={`mt-0.5 text-sm font-medium ${valueColor}`}>{value}</p>
        </div>
    </div>
);

const getStatusActions = (status: AdminStatus) => {
    if (status === "ACTIVE") {
        return [
            { action: "suspend" as const, label: "Suspend", icon: NoSymbolIcon, color: "amber" },
            { action: "deactivate" as const, label: "Deactivate", icon: NoSymbolIcon, color: "slate" },
        ];
    }

    if (status === "SUSPENDED") {
        return [{ action: "reactivate" as const, label: "Reactivate", icon: PowerIcon, color: "emerald" }];
    }

    if (status === "DEACTIVATED") {
        return [{ action: "activate" as const, label: "Activate", icon: PowerIcon, color: "emerald" }];
    }

    return [];
};

export const AdminDetailsRoute = () => {
    const { adminId } = useParams<{ adminId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
    const [statusDialog, setStatusDialog] = useState<UpdateAdminStatusAction | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ["adminDetails", adminId],
        queryFn: () => getAdminDetails(adminId!),
        enabled: !!adminId,
    });
    const permissionCatalogQuery = useQuery({
        queryKey: ["permissions-catalog"],
        queryFn: getPermissionsCatalog,
    });

    useEffect(() => {
        const state = location.state as { flashMessage?: string } | null;
        if (state?.flashMessage) {
            setFlashMessage({ type: "success", text: state.flashMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [location.pathname, location.state, navigate]);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "Never";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatShortDate = (dateString: string | null) => {
        if (!dateString) return "Never";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    if (isLoading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Loading admin details...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !data?.data) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Failed to load admin details
                    </p>
                    <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                        Please try again later.
                    </p>
                    <button
                        onClick={() => navigate("/admin-management")}
                        className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
                    >
                        Back to Admins
                    </button>
                </div>
            </div>
        );
    }

    const admin: Admin = data.data;
    const isCurrentAccount = admin.adminId === user?.userId;
    const isSuperAdmin = user?.role === "SUPER_ADMIN";
    const statusActions = getStatusActions(admin.status);
    const permissionCatalog = permissionCatalogQuery.data?.data.permissions;

    const roleGradients = {
        SUPER_ADMIN: "from-rose-500 to-rose-600",
        ADMIN: "from-emerald-500 to-emerald-600",
        MODERATOR: "from-blue-500 to-blue-600",
    };

    const roleGradient = roleGradients[admin.role as keyof typeof roleGradients] || roleGradients.MODERATOR;

    return (
        <div className="space-y-6">
            {flashMessage ? (
                <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium ${
                        flashMessage.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        {flashMessage.type === "success" ? (
                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                        ) : (
                            <XCircleIcon className="h-4 w-4 shrink-0" />
                        )}
                        <span>{flashMessage.text}</span>
                    </div>
                    <button
                        onClick={() => setFlashMessage(null)}
                        className="ml-4 rounded p-0.5 opacity-60 transition hover:opacity-100"
                        aria-label="Dismiss"
                    >
                        <XCircleIcon className="h-4 w-4" />
                    </button>
                </div>
            ) : null}

            {/* Header Navigation */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate("/admin-management")}
                    className="flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                    Back to Admins
                </button>
                <div className="flex gap-3">
                    <button
                        onClick={() =>
                            navigate(`/admin-management/${admin.adminId}/edit`, {
                                state: { from: location.pathname },
                            })
                        }
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                        <PencilSquareIcon className="h-4 w-4" />
                        Edit Profile
                    </button>
                    {isCurrentAccount ? (
                        <button
                            onClick={() => navigate("/settings")}
                            className="inline-flex items-center gap-2 rounded-lg border border-primary-200 bg-white px-4 py-2 text-sm font-medium text-primary-700 transition hover:bg-primary-50 dark:border-primary-800 dark:bg-slate-800 dark:text-primary-300 dark:hover:bg-primary-900/20"
                        >
                            <Cog6ToothIcon className="h-4 w-4" />
                            Account Settings
                        </button>
                    ) : null}
                    {isSuperAdmin && !isCurrentAccount ? (
                        <>
                            <button
                                onClick={() => setResetPasswordModalOpen(true)}
                                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-amber-600"
                            >
                                <KeyIcon className="h-4 w-4" />
                                Reset Password
                            </button>
                            {statusActions.map(({ action, label, icon: Icon, color }) => (
                                <button
                                    key={action}
                                    onClick={() => setStatusDialog(action)}
                                    className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition ${
                                        color === "emerald"
                                            ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                            : color === "amber"
                                              ? "border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20"
                                              : "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {label}
                                </button>
                            ))}
                            <button
                                onClick={() => setDeleteDialogOpen(true)}
                                className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                            >
                                <TrashIcon className="h-4 w-4" />
                                Delete Admin
                            </button>
                        </>
                    ) : null}
                </div>
            </div>

            {/* Profile Header Card */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className={`bg-gradient-to-r ${roleGradient} px-8 py-8`}>
                    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                        {/* Avatar */}
                        <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white/20 text-3xl font-bold text-white backdrop-blur-sm ring-4 ring-white/20">
                            {admin.firstName[0]}
                            {admin.lastName[0]}
                        </div>

                        {/* Info */}
                        <div className="flex-1 text-center sm:text-left">
                            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
                                <h1 className="text-2xl font-bold text-white">
                                    {admin.fullName}
                                </h1>
                                <StatusBadge status={admin.status} />
                            </div>
                            <p className="mt-2 text-sm text-white/80">{admin.email}</p>
                            <div className="mt-4">
                                <RoleBadge role={admin.role} />
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="flex gap-6 text-center sm:gap-8">
                            <div>
                                <p className="text-2xl font-bold text-white">
                                    {admin.emailVerified ? "Yes" : "No"}
                                </p>
                                <p className="text-sm text-white/70">Email Verified</p>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">
                                    {admin.effectivePermissions.length}
                                </p>
                                <p className="text-sm text-white/70">Effective Permissions</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Admin ID */}
                <div className="border-t border-slate-200 bg-slate-50 px-8 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                            <IdentificationIcon className="h-4 w-4" />
                            <span>Admin ID:</span>
                            <code className="rounded bg-slate-200 px-2 py-0.5 font-mono text-xs dark:bg-slate-700">
                                {admin.adminId}
                            </code>
                        </div>
                        <button
                            onClick={() => admin.adminId && navigator.clipboard.writeText(admin.adminId)}
                            disabled={!admin.adminId}
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                        >
                            Copy ID
                        </button>
                    </div>
                </div>
            </div>

            {/* Details Grid */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Contact Information */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
                        <EnvelopeIcon className="h-5 w-5 text-primary-500" />
                        Contact Information
                    </h2>
                    <div className="space-y-5">
                        <InfoCard
                            icon={EnvelopeIcon}
                            label="Email Address"
                            value={
                                <span className="flex items-center gap-2">
                                    {admin.email}
                                    {admin.emailVerified && (
                                        <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                                    )}
                                </span>
                            }
                        />
                        <InfoCard
                            icon={PhoneIcon}
                            label="Phone Number"
                            value={admin.phoneNumber || "—"}
                        />
                    </div>
                </div>

                {/* Access & Permissions */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
                        <LockClosedIcon className="h-5 w-5 text-primary-500" />
                        Access & Permissions
                    </h2>
                    <div className="space-y-5">
                        <InfoCard
                            icon={ShieldCheckIcon}
                            label="Role"
                            value={<RoleBadge role={admin.role} />}
                        />
                        <div className="flex items-start gap-4">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                                <GlobeAltIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                            </div>
                            <div className="min-w-0 flex-1 space-y-4">
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                        Explicit Permissions
                                    </p>
                                    <div className="mt-2">
                                        <PermissionSummary
                                            permissions={admin.permissions}
                                            catalog={permissionCatalog}
                                            emptyLabel="No explicit permissions assigned"
                                            maxVisible={6}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                        Effective Permissions
                                    </p>
                                    <div className="mt-2">
                                        <PermissionSummary
                                            permissions={admin.effectivePermissions}
                                            catalog={permissionCatalog}
                                            emptyLabel="No effective permissions returned"
                                            maxVisible={6}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <InfoCard
                            icon={CheckCircleIcon}
                            label="Email Verification"
                            value={
                                admin.emailVerified ? (
                                    <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                                        <CheckCircleIcon className="h-4 w-4" />
                                        Verified
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                                        <XCircleIcon className="h-4 w-4" />
                                        Pending Verification
                                    </span>
                                )
                            }
                        />
                    </div>
                </div>

                {/* Account Timeline */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
                        <CalendarIcon className="h-5 w-5 text-primary-500" />
                        Account Timeline
                    </h2>
                    <div className="space-y-5">
                        <InfoCard
                            icon={CalendarIcon}
                            label="Account Created"
                            value={formatDate(admin.createdAt)}
                        />
                        <InfoCard
                            icon={ClockIcon}
                            label="Last Updated"
                            value={formatDate(admin.updatedAt)}
                        />
                        {admin.createdBy && (
                            <InfoCard
                                icon={UserCircleIcon}
                                label="Created By"
                                value={
                                    <code className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs dark:bg-slate-800">
                                        {admin.createdBy}
                                    </code>
                                }
                            />
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-6 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
                        <ShieldCheckIcon className="h-5 w-5 text-primary-500" />
                        Quick Actions
                    </h2>
                    <div className="space-y-3">
                        <button
                            onClick={() =>
                                navigate(`/admin-management/${admin.adminId}/edit`, {
                                    state: { from: location.pathname },
                                })
                            }
                            className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-primary-300 hover:bg-primary-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-primary-600 dark:hover:bg-primary-900/20"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                                <PencilSquareIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <p className="font-medium text-slate-900 dark:text-white">
                                    Edit Profile
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Update name or phone number
                                </p>
                            </div>
                        </button>
                        {isCurrentAccount ? (
                            <button
                                onClick={() => navigate("/settings")}
                                className="flex w-full items-center gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4 text-left transition hover:border-primary-300 dark:border-primary-900/60 dark:bg-primary-950/20 dark:hover:border-primary-800"
                            >
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                                    <Cog6ToothIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-900 dark:text-white">
                                        Manage Your Account
                                    </p>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Open Settings to review your profile and change your password
                                    </p>
                                </div>
                            </button>
                        ) : null}
                        {isSuperAdmin && !isCurrentAccount ? (
                            <>
                                <button
                                    onClick={() => setResetPasswordModalOpen(true)}
                                    className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-amber-300 hover:bg-amber-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-amber-600 dark:hover:bg-amber-900/20"
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
                                        <KeyIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900 dark:text-white">
                                            Reset Password
                                        </p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            Set a new password for this admin
                                        </p>
                                    </div>
                                </button>
                                {statusActions.map(({ action, label, icon: Icon, color }) => (
                                    <button
                                        key={action}
                                        onClick={() => setStatusDialog(action)}
                                        className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition ${
                                            color === "emerald"
                                                ? "border-emerald-200 bg-emerald-50 hover:border-emerald-300 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:hover:border-emerald-800"
                                                : color === "amber"
                                                  ? "border-amber-200 bg-amber-50 hover:border-amber-300 dark:border-amber-900/60 dark:bg-amber-950/20 dark:hover:border-amber-800"
                                                  : "border-slate-200 bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600"
                                        }`}
                                    >
                                        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                                            color === "emerald"
                                                ? "bg-emerald-100 dark:bg-emerald-900/30"
                                                : color === "amber"
                                                  ? "bg-amber-100 dark:bg-amber-900/30"
                                                  : "bg-slate-200 dark:bg-slate-700"
                                        }`}>
                                            <Icon className={`h-5 w-5 ${
                                                color === "emerald"
                                                    ? "text-emerald-600 dark:text-emerald-400"
                                                    : color === "amber"
                                                      ? "text-amber-600 dark:text-amber-400"
                                                      : "text-slate-700 dark:text-slate-200"
                                            }`} />
                                        </div>
                                        <div>
                                            <p className="font-medium text-slate-900 dark:text-white">
                                                {label}
                                            </p>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                {action === "suspend"
                                                    ? "Temporarily remove console access"
                                                    : action === "deactivate"
                                                      ? "Mark this admin account as inactive"
                                                      : "Restore this admin account to active status"}
                                            </p>
                                        </div>
                                    </button>
                                ))}
                                <button
                                    onClick={() => setDeleteDialogOpen(true)}
                                    className="flex w-full items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-left transition hover:border-rose-300 dark:border-rose-900/60 dark:bg-rose-950/20 dark:hover:border-rose-800"
                                >
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100 dark:bg-rose-900/30">
                                        <TrashIcon className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900 dark:text-white">
                                            Delete Admin
                                        </p>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            Permanently remove this administrator account
                                        </p>
                                    </div>
                                </button>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Modals */}
            <ResetPasswordModal
                isOpen={resetPasswordModalOpen}
                onClose={() => setResetPasswordModalOpen(false)}
                admin={admin}
            />
            <AdminStatusModal
                isOpen={!!statusDialog}
                onClose={() => setStatusDialog(null)}
                admin={admin}
                action={statusDialog}
                onCompleted={(message) => setFlashMessage({ type: "success", text: message })}
            />
            <DeleteAdminModal
                isOpen={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                admin={admin}
                onCompleted={(message) => {
                    navigate("/admin-management", { state: { flashMessage: message } });
                }}
            />
        </div>
    );
};
